import { useEffect, useRef } from 'react'
import { supabase } from '../../shared/lib/supabase'
import type { RealtimeChannel } from '@supabase/supabase-js'

// ─── Types ──────────────────────────────────────────────────

export interface NewMessagePayload {
  messageId: string
  conversationId: string
  content: string
  timestamp: string
  role?: 'user' | 'assistant'
  briefingCard?: Record<string, unknown>
  triageEmail?: Record<string, unknown>
  sourceEmailId?: string
}

// ─── Hook ───────────────────────────────────────────────────

/**
 * Subscribe to async push events via Supabase Realtime.
 * Handles events that arrive outside the request-response cycle:
 * message.new (briefing cards, cron-inserted messages), etc.
 *
 * Chat streaming (deltas, tool activity, completion) is handled
 * via SSE on the POST /chat/message endpoint — not here.
 */
export function useRealtimeEvents(
  userId: string | undefined,
  onNewMessage?: (payload: NewMessagePayload) => void,
): void {
  const channelRef = useRef<RealtimeChannel | null>(null)
  const cleanupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Stable callback ref to avoid resubscribing on every render
  const onNewMessageRef = useRef(onNewMessage)
  useEffect(() => {
    onNewMessageRef.current = onNewMessage
  })

  useEffect(() => {
    if (!userId) return

    // Cancel any pending cleanup from StrictMode's previous unmount —
    // this keeps the WebSocket transport alive across double-invocation.
    if (cleanupTimerRef.current) {
      clearTimeout(cleanupTimerRef.current)
      cleanupTimerRef.current = null
    }

    // If we already have a channel from a previous mount (StrictMode re-mount),
    // reuse it instead of creating a new one with duplicate listeners.
    if (channelRef.current) {
      return () => {
        cleanupTimerRef.current = setTimeout(() => {
          if (channelRef.current) {
            supabase.removeChannel(channelRef.current)
            channelRef.current = null
          }
          cleanupTimerRef.current = null
        }, 200)
      }
    }

    const channelName = `status:${userId}`
    const channel = supabase.channel(channelName)
    channelRef.current = channel

    channel.on('broadcast', { event: 'message.new' }, ({ payload }) => {
      onNewMessageRef.current?.(payload as NewMessagePayload)
    })

    channel.subscribe()

    return () => {
      // Delay removal so the WebSocket transport survives React StrictMode's
      // unmount/remount cycle. If the component re-mounts, the timer is cancelled
      // above and the existing channel is reused.
      cleanupTimerRef.current = setTimeout(() => {
        if (channelRef.current) {
          supabase.removeChannel(channelRef.current)
          channelRef.current = null
        }
        cleanupTimerRef.current = null
      }, 200)
    }
  }, [userId])
}
